import React, { useState, useEffect } from 'react';
import TablaProyectos from './TablaProyectos/TablaProyectos';
import WelcomeTitle from './WelcomeTitle/WelcomeTitle';
import IProyecto from '../../interfaces/IProyecto';
import { SBU_OPTIONS } from './Filtros/FiltroSBU';
import FiltroSBU from './Filtros/FiltroSBU';
import User from '../../interfaces/User';
import Header from '../Header/Header';
import Loader from './Loader/Loader';
import { History } from 'history';
import './Inicio.css';
import { useSelector } from 'react-redux';
import PlanificacionClient from '../../core/planificacionClient';
import firebase from 'firebase';
import ErrorComponent from '../Error/Error';
import SearchBar from '../SearchBar/SearchBar';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import WidgetAccesos from '../WidgetAccesos/WidgetAccesos';
import { useLocation } from "react-router-dom";
import { useQuery } from '@tanstack/react-query';
import { QUERY_TIMES_MS } from '../../config/queryConfig';
import WorkInProgress from '../WorkInProgress/WorkInProgress';

interface Props {
  user: User;
  history: History;
  proyectos: Array<IProyecto>;
  setProyecto: (p: IProyecto) => void;
}

export default function Inicio(props: Props) {
  const [search, setSearch]: any = useState([]);
  const [sbuFilter, setSbuFilter] = useState(SBU_OPTIONS.TODOS);
  const [proyectos, setProyectos] = useState({ cerrados: false, internos: false });
  const query = new URLSearchParams(useLocation().search);
  const getParamProyecto = query.get('q');
  const enConstruccion = false; // Cambia a false cuando la página esté lista

  const GetProyectosCerradosInternos = async () => {
    const firebaseToken = await firebase.auth().currentUser.getIdTokenResult();
    const cliente = new PlanificacionClient(firebaseToken.token);
    const dataProyectos = await cliente.getProyectosCerradosInternosCore(proyectos.cerrados, proyectos.internos);
    return dataProyectos;
  };

  const { data: cachedProyectos, dataUpdatedAt, isFetching, isLoading } = useQuery({
    queryKey: ['proyectos-home', proyectos.cerrados, proyectos.internos],
    queryFn: GetProyectosCerradosInternos,
    staleTime: QUERY_TIMES_MS.HOME_REFRESH,
    refetchOnWindowFocus: true,
    refetchInterval: QUERY_TIMES_MS.HOME_REFRESH,
  });

  React.useEffect(() => {
    if (dataUpdatedAt) {
      localStorage.setItem('lastUpdated_home', String(dataUpdatedAt));
    }
  }, [dataUpdatedAt]);

  const lastUpdatedHome = props.proyectos[0]?.fechaGeneracion;

  useEffect(() => {
    if (getParamProyecto && cachedProyectos?.length) {
      handleChangeSearch({ target: { value: getParamProyecto } });
    }
  }, [cachedProyectos]);

  const onClickProyecto = (idProject: string, proyecto: IProyecto): void => {
    props.setProyecto(proyecto);
  };

  const onSBUFilterChange = (filterKey: string): void => {
    setSbuFilter(filterKey);
  };

  const handleChangeSearch = (event) => {
    const value = event.target.value.toLowerCase();
    if (!value) return setSearch([]);
    const source = cachedProyectos ?? [];
    const filtered = source.filter((p) =>
      (p.nombre ?? '').toLowerCase().includes(value) ||
      (p.pm ?? '').toLowerCase().includes(value) ||
      (p.cliente ?? '').toLowerCase().includes(value) ||
      (p.sbu ?? '').toLowerCase().includes(value)
    );
    setSearch(filtered);
  };

  const filterProyectos = (proyectos) => {
    if (!Array.isArray(proyectos)) return [];
    if (sbuFilter === SBU_OPTIONS.TODOS) return proyectos;
    if (sbuFilter === SBU_OPTIONS.PROYECTO_INTERNO)
      return proyectos.filter(p => p.tipo === "Proyecto Interno");
    return proyectos.filter(p => p.sbu === sbuFilter);
  };

  const handleClickProyectosCerrados = () => {
    setProyectos(prev => ({ ...prev, cerrados: !prev.cerrados }));
  };

  const handleClickProyectosInternos = () => {
    setProyectos(prev => ({ ...prev, internos: !prev.internos }));
  };

  const getProyectos = () => {
    const base = cachedProyectos ?? [];
    const filtrados = filterProyectos(base);
    return search.length ? search : filtrados;
  };

  const projectsError: any = useSelector<any>(state => state.generalReducer.statusCode);
  
  if (enConstruccion) {
    return <WorkInProgress />;
  }

  return (
    <div className="inicio-main-container">
      <Header
        history={props.history}
        userName={props.user.displayName}
        userImageUrl={props.user.photoURL}
        ultimaActualizacion={lastUpdatedHome}
      />
      <div className="inicio-body">
        <div className="inicio-left-container">
          <div className='inicio-search'>
            <SearchBar handleChangeSearch={handleChangeSearch} getParamProyecto={getParamProyecto}/>
            <div className="inicio-filters-container">
              <WelcomeTitle userName={props.user.displayName} />
              <div className="inicio-filters">
                <div className="check-proyectos-cerrados" onClick={handleClickProyectosCerrados}>
                  {proyectos.cerrados ? <ToggleOnIcon className="icon-check-cerrados-on" /> : <ToggleOffIcon className="icon-check-cerrados-off" />}
                  Ver proyectos cerrados
                </div>
                <div className="check-proyectos-internos" onClick={handleClickProyectosInternos}>
                  {proyectos.internos ? <ToggleOnIcon className="icon-check-cerrados-on" /> : <ToggleOffIcon className="icon-check-cerrados-off" />}
                  Ver proyectos internos
                </div>
                <FiltroSBU proyectos={cachedProyectos ?? []} SBU={sbuFilter} onSBUChange={onSBUFilterChange}/>
              </div>
            </div>
          </div>
          {projectsError.statusCode && projectsError.statusCode !== 204 && (
            <ErrorComponent statusCode={projectsError} />
          )}
          
          {(isLoading || isFetching) && !cachedProyectos ? (
            <div className="tabla-proyectos-skeleton">
              <div className="skeleton-header">
                <div className="skeleton-header-cell"></div>
                <div className="skeleton-header-cell"></div>
                <div className="skeleton-header-cell"></div>
                <div className="skeleton-header-cell"></div>
                <div className="skeleton-header-cell"></div>
              </div>
              {[...Array(6)].map((_, index) => (
                <div key={index} className="skeleton-row">
                  <div className="skeleton-cell skeleton-cell-large"></div>
                  <div className="skeleton-cell skeleton-cell-medium"></div>
                  <div className="skeleton-cell skeleton-cell-medium"></div>
                  <div className="skeleton-cell skeleton-cell-small"></div>
                  <div className="skeleton-cell skeleton-cell-small"></div>
                </div>
              ))}
            </div>
          ) : (
            <TablaProyectos
              error={projectsError}
              onClickProyecto={onClickProyecto}
              proyectos={getProyectos()}
            />
          )}
          
          <Loader show={!cachedProyectos && !isFetching && !isLoading} />
        </div>
      </div>
      <WidgetAccesos/>
    </div>
  );
}